/**
 * Linear run-rate projection of a month-to-date value to the full month:
 * (value so far / completed days) * days in month.
 * Today is not counted as its data is not in yet. Returns null on the 1st
 * (no completed day to extrapolate from).
 */
export const projectToFullMonth = (monthToDate: number, now: Date = new Date()): number | null => {
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const completedDays = now.getDate() - 1;
  if (completedDays === 0) {
    return null;
  }
  return (monthToDate / completedDays) * daysInMonth;
};

/**
 * Percent change of the projected full-month value vs. last month's total.
 * Returns null when either value is missing, last month's total is zero, or no day is complete yet.
 */
export const projectedChangePercent = (
  monthToDate: unknown,
  previousMonthTotal: unknown,
  now: Date = new Date()
): number | null => {
  // Number(null) is 0, which would read as a -100 % drop; treat missing as unknown.
  if (monthToDate == null || previousMonthTotal == null) {
    return null;
  }
  const current = Number(monthToDate);
  const previous = Number(previousMonthTotal);
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) {
    return null;
  }
  const projected = projectToFullMonth(current, now);
  return projected == null ? null : ((projected - previous) / previous) * 100;
};
